import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';

import { SessionAccountScope } from '../enums/session-end-reason.enum';
import { StoredUserEntity } from '../utils/local-storage.utils';

/** A device row as returned by `POST api/heroes/logout`. */
export interface SessionDeviceRecord {
    _id: string;
    deviceName: string;
    deviceType: string;
    macAddress: string;
    deviceToken: string;
    loginToken: string;
    lastActivityTime: string;
    lastLoginTime: string;
    entityIds: string[];
}

/** Request body for `POST api/heroes/logout`. */
export interface RevokeSessionRequest {
    macAddress: string;
}

/** Response body for `POST api/heroes/logout` — `data` is the caller's remaining devices. */
export interface RevokeSessionResponse {
    success: boolean;
    message: string;
    messageCode: number;
    data: SessionDeviceRecord[];
}

/**
 * One stored account inside the `userData` list. Extends the shared shape with
 * the two fields session teardown needs: `phone` groups sibling accounts, and
 * `devices` is emptied when their sessions are revoked.
 */
export interface StoredSessionAccount extends StoredUserEntity {
    phone?: string;
    devices?: SessionDeviceRecord[];
}

/**
 * How `SessionService` reaches the logout endpoint.
 *
 * This exists as a token-backed interface so `libs/core` never imports
 * `libs/api` — the dependency direction is `core <- api <- feature <- ui`.
 * `AuthApiService` implements it and `appConfig` (app.config.ts) binds the two together.
 */
export interface SessionLogoutTransport {
    revokeDeviceSession(request: RevokeSessionRequest): Observable<RevokeSessionResponse>;
}

export const SESSION_LOGOUT_TRANSPORT = new InjectionToken<SessionLogoutTransport>(
    'SESSION_LOGOUT_TRANSPORT'
);

/**
 * The complete description of what ending a session does. Every behavioural
 * difference between logout, logout-all, session-expiry and account-switching
 * is expressed here — there is no other branching in `SessionService`.
 */
export interface SessionEndPolicy {
    /** Whether to tell the server to revoke this device. False when the session is already dead. */
    revokeOnServer: boolean;

    /** Which stored accounts get their device list emptied. */
    accountScope: SessionAccountScope;

    /** Toast shown to the user, or `null` for a silent teardown. */
    notice: SessionNotice | null;

    /** Whether to redirect to the welcome screen. False when the caller owns navigation. */
    navigateToWelcome: boolean;
}

/** Title and body for the toast shown after a session ends. */
export interface SessionNotice {
    title: string;
    message: string;
}

/**
 * Abstraction over "close all open modals" so that `libs/core` never imports
 * UI modal libraries directly.
 *
 * The concrete implementation (`AppModalCloser`) lives in `app/` and is bound
 * to this token in `appConfig` (app.config.ts), following the same pattern as
 * `SESSION_LOGOUT_TRANSPORT`.
 */
export interface SessionModalCloser {
    closeAll(): void;

    /**
     * Turns on the guard that keeps dialogs from coming back after `closeAll`.
     *
     * Closing is not enough on its own: Bootstrap re-attaches a dialog to
     * `<body>` if its own show callback lands after Angular has already removed
     * the element, which resurrects a dead screen's modal on top of whatever
     * the user was redirected to. The guard stays on for the whole teardown and
     * is released by the next sign-in.
     */
    setTeardownGuard(isActive: boolean): void;
}

export const SESSION_MODAL_CLOSER = new InjectionToken<SessionModalCloser>(
    'SESSION_MODAL_CLOSER'
);
